import { createTheme } from '@mui/material/styles';
import type { PaletteMode } from '@mui/material';

/**
 * The single source of colour truth. `tailwind.config.ts` mirrors these values so a
 * Tailwind utility and an MUI component never disagree about what "brand green" is.
 */
export const brand = {
  50: '#f2f7f4',
  100: '#dfeae3',
  200: '#bfd6c8',
  300: '#94bba4',
  400: '#679c7e',
  500: '#468062',
  600: '#33664d',
  700: '#2a5240',
  800: '#244334',
  900: '#1f382c',
} as const;

export const accent = {
  500: '#c9762f',
  600: '#a95f22',
} as const;

export const createAppTheme = (mode: PaletteMode = 'light') =>
  createTheme({
    palette: {
      mode,
      primary: {
        main: brand[600],
        light: brand[400],
        dark: brand[800],
        contrastText: '#ffffff',
      },
      secondary: {
        main: accent[500],
        dark: accent[600],
        contrastText: '#ffffff',
      },
      success: { main: '#2e7d32' },
      warning: { main: '#ed6c02' },
      error: { main: '#c62828' },
      background: {
        default: mode === 'light' ? '#f6f7f6' : '#121513',
        paper: mode === 'light' ? '#ffffff' : '#1b1f1c',
      },
    },
    shape: { borderRadius: 8 },
    typography: {
      fontFamily: "'Roboto', system-ui, sans-serif",
      h1: { fontSize: '1.75rem', fontWeight: 600 },
      h2: { fontSize: '1.4rem', fontWeight: 600 },
      h3: { fontSize: '1.2rem', fontWeight: 600 },
      h6: { fontSize: '1rem', fontWeight: 600 },
      button: { textTransform: 'none', fontWeight: 500 },
    },
    components: {
      MuiButton: {
        defaultProps: { disableElevation: true },
      },
      MuiTextField: {
        defaultProps: { size: 'small', fullWidth: true },
      },
      MuiSelect: {
        defaultProps: { size: 'small' },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: { fontWeight: 600 },
        },
      },
    },
  });
